'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@sprd/sprd-component-kit/v2';
import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocale } from 'next-intl';
import { PlpBannerModule } from '@/types/amplience';
import { useActivateVoucher } from '@/hooks/useCartHooks';
import { isCurrentPromoActivatedAtom, isPromoExpiredAtom, openPromoPopupAtom, promoConfigAtom } from '@/atoms/promoAtom';
import AmplienceLink from '@/components/Common/AmplienceLink';
import DynamicIcon, { isDynamicIcon } from '@/components/Common/DynamicIcon';
import { AmplienceResponsiveImage } from '@/components/ResponsiveImage/AmplienceResponsiveImage';
import { Locale, getApiLocale } from '@/utility/locale';
import { tracker } from '@/utility/tracking/tracker';
const IMAGE_WIDTHS_DESKTOP = [640, 800, 1024, 1280];
const IMAGE_WIDTHS_MOBILE = [320, 480, 640, 768];
type PlpBannerProps = {
  content: PlpBannerModule;
  className?: string;
};
const CtaIcon = t0 => {
  const $ = _c(2);
  const {
    icon
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = isDynamicIcon(icon) ? <DynamicIcon icon={icon} aria-hidden="true" className="size-4 shrink-0" /> : null;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export default function PlpBanner(t0) {
  const $ = _c(65);
  const {
    content,
    className
  } = t0;
  const locale = useLocale() as Locale;
  let t1;
  if ($[0] !== locale) {
    t1 = getApiLocale(locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const market = t1;
  const bannerId = content._meta.deliveryId || content._meta.deliveryKey || content._meta.name;
  const hasTrackedShown = useRef(false);
  const promoSlot = useAtomValue(promoConfigAtom);
  const isPromoExpired = useAtomValue(isPromoExpiredAtom);
  const isActivated = useAtomValue(isCurrentPromoActivatedAtom);
  const setOpenPromoPopup = useSetAtom(openPromoPopupAtom);
  const {
    applyVoucher
  } = useActivateVoucher();
  const voucherCode = promoSlot?.content["promo-tool"]?.campaignDetails?.code;
  const canActivateCoupon = Boolean(voucherCode) && !isPromoExpired;
  let t2;
  let t3;
  if ($[2] !== bannerId || $[3] !== market) {
    t2 = () => {
      if (hasTrackedShown.current) {
        return;
      }
      hasTrackedShown.current = true;
      tracker.trackFeature({
        featureUsed: "plp-banner-shown",
        detail: {
          bannerId,
          market
        }
      });
    };
    t3 = [bannerId, market];
    $[2] = bannerId;
    $[3] = market;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== bannerId || $[7] !== market) {
    t4 = target => {
      tracker.trackFeature({
        featureUsed: "plp-banner-clicked",
        detail: {
          bannerId,
          market,
          target
        }
      });
    };
    $[6] = bannerId;
    $[7] = market;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const trackClick = t4;
  const backgroundColor = content.backgroundColor || "#D01C00";
  const legacyConditionsLink = Array.isArray(content.conditionsLink) ? content.conditionsLink[0] : content.conditionsLink;
  let t5;
  if ($[9] !== content.cta.conditionsLabel || $[10] !== content.cta.type || $[11] !== legacyConditionsLink?.label) {
    t5 = content.cta.type === "activateCoupon" ? content.cta.conditionsLabel?.trim() || legacyConditionsLink?.label?.trim() : undefined;
    $[9] = content.cta.conditionsLabel;
    $[10] = content.cta.type;
    $[11] = legacyConditionsLink?.label;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const conditionsLabel = t5;
  let t6;
  if ($[13] !== applyVoucher || $[14] !== isActivated || $[15] !== trackClick) {
    t6 = () => {
      if (isActivated) {
        return;
      }
      trackClick("activateCoupon");
      applyVoucher();
    };
    $[13] = applyVoucher;
    $[14] = isActivated;
    $[15] = trackClick;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleActivateCoupon = t6;
  let t7;
  if ($[17] !== canActivateCoupon || $[18] !== content || $[19] !== handleActivateCoupon || $[20] !== isActivated || $[21] !== trackClick) {
    t7 = () => {
      const {
        cta
      } = content;
      if (cta.type === "landing") {
        return <AmplienceLink link={cta.link} className="inline-flex h-12 w-full items-center justify-center gap-2 bg-black px-6 text-base font-bold text-white uppercase hover:bg-gray-900 min-[1000px]:w-auto" onClick={() => trackClick(cta.link.url)}>{cta.link.icon && <CtaIcon icon={cta.link.icon} />}{cta.link.label}</AmplienceLink>;
      }
      if (!canActivateCoupon) {
        return null;
      }
      return <Button type="button" size="l" variant="primary" className="w-full gap-2 min-[1000px]:w-auto" onClick={handleActivateCoupon} aria-disabled={isActivated}><CtaIcon icon={isActivated ? cta.activatedIcon : cta.icon} />{isActivated ? cta.activatedLabel : cta.label}</Button>;
    };
    $[17] = canActivateCoupon;
    $[18] = content;
    $[19] = handleActivateCoupon;
    $[20] = isActivated;
    $[21] = trackClick;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const renderCta = t7;
  let t8;
  if ($[23] !== className) {
    t8 = clsx("w-full overflow-hidden", className);
    $[23] = className;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== backgroundColor) {
    t9 = {
      backgroundColor
    };
    $[25] = backgroundColor;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = content.headline;
  let t11;
  if ($[27] !== content.headline) {
    t11 = <h2 className="font-made text-[28px] leading-8 font-black uppercase min-[1000px]:text-[40px] min-[1000px]:leading-[48px]">{content.headline}</h2>;
    $[27] = content.headline;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== content.subheadline) {
    t12 = content.subheadline && <p className="text-sm leading-5 font-bold not-italic min-[1000px]:text-[18px] min-[1000px]:leading-7">{content.subheadline}</p>;
    $[29] = content.subheadline;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <div className="flex flex-col gap-2">{t11}{t12}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== renderCta) {
    t14 = renderCta();
    $[34] = renderCta;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== conditionsLabel || $[37] !== setOpenPromoPopup || $[38] !== trackClick) {
    t15 = conditionsLabel && <button type="button" className="text-center text-xs leading-4 font-semibold text-white underline underline-offset-2 min-[1000px]:text-left" onClick={() => {
      trackClick("conditions");
      setOpenPromoPopup(true);
    }}>{conditionsLabel}</button>;
    $[36] = conditionsLabel;
    $[37] = setOpenPromoPopup;
    $[38] = trackClick;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t14 || $[41] !== t15) {
    t16 = <div className="flex flex-col items-stretch gap-2 min-[1000px]:items-start">{t14}{t15}</div>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t13 || $[44] !== t16) {
    t17 = <div className="flex min-w-0 flex-1 flex-col justify-center gap-3 text-white min-[1000px]:gap-4">{t13}{t16}</div>;
    $[43] = t13;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = clsx("relative shrink-0 overflow-hidden", "h-[107px] w-full rounded-br-[80px] min-[640px]:h-[140px]", "min-[1000px]:h-auto min-[1000px]:min-h-[172px] min-[1000px]:w-1/2 min-[1000px]:self-stretch min-[1000px]:rounded-[80px_0_80px_0]");
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== content.mobileImage.image) {
    t19 = {
      src: content.mobileImage.image,
      sizes: "100vw",
      media: "(max-width: 999px)",
      widths: IMAGE_WIDTHS_MOBILE
    };
    $[47] = content.mobileImage.image;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== content.desktopImage.image) {
    t20 = {
      src: content.desktopImage.image,
      sizes: "(min-width: 1000px) 50vw, 100vw",
      media: "(min-width: 1000px)",
      widths: IMAGE_WIDTHS_DESKTOP
    };
    $[49] = content.desktopImage.image;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t19 || $[52] !== t20) {
    t21 = [t19, t20];
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  const t22 = content.desktopImage.altText || content.mobileImage.altText || content.headline;
  let t23;
  if ($[54] !== t21 || $[55] !== t22) {
    t23 = <div className={t18}><AmplienceResponsiveImage className="absolute inset-0 block size-full max-w-none object-cover min-[1000px]:object-none" render="server-side" source={t21} alt={t22} loading="eager" /></div>;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t17 || $[58] !== t23) {
    t24 = <div className="flex w-full flex-col-reverse gap-4 p-4 min-[1000px]:flex-row min-[1000px]:items-stretch min-[1000px]:gap-4 min-[1000px]:p-6">{t17}{t23}</div>;
    $[57] = t17;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== content.headline || $[61] !== t24 || $[62] !== t8 || $[63] !== t9) {
    t25 = <aside data-type="PlpBanner" className={t8} style={t9} aria-label={t10}>{t24}</aside>;
    $[60] = content.headline;
    $[61] = t24;
    $[62] = t8;
    $[63] = t9;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  return t25;
}